import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveInstrument } from '../../store/uiSlice';
import { fmtP } from '../../utils/formatters';
import { INSTRUMENTS } from '../../utils/constants';

// Currency to flag mapping
const CURRENCY_FLAGS = {
  USD: "us", EUR: "eu", GBP: "gb", JPY: "jp", CHF: "ch",
  AUD: "au", CAD: "ca", NZD: "nz", CNY: "cn", CNH: "cn",
  HKD: "hk", SGD: "sg", SEK: "se", NOK: "no", DKK: "dk",
  PLN: "pl", TRY: "tr", RUB: "ru", ZAR: "za", MXN: "mx",
  INR: "in", BRL: "br", KRW: "kr", THB: "th", IDR: "id",
  MYR: "my", PHP: "ph", VND: "vn", ILS: "il", CZK: "cz",
  HUF: "hu", RON: "ro", BGN: "bg", HRK: "hr", ISK: "is",
  CLP: "cl", COP: "co", PEN: "pe", ARS: "ar", EGP: "eg",
  NGN: "ng", KES: "ke", MAD: "ma", TND: "tn", GHS: "gh",
  QAR: "qa", AED: "ae", SAR: "sa", KWD: "kw", BHD: "bh",
  OMR: "om", JOD: "jo", LBP: "lb", IQD: "iq", IRR: "ir",
  AFN: "af", PKR: "pk", BDT: "bd", LKR: "lk", MMK: "mm",
  KHR: "kh", LAK: "la", TWD: "tw", UAH: "ua", KZT: "kz",
  UZS: "uz", AZN: "az", GEL: "ge", AMD: "am", BYN: "by",
  MDL: "md", MKD: "mk", ALL: "al", RSD: "rs", BAM: "ba",
  MNT: "mn", BND: "bn", BWP: "bw", CVE: "cv", ETB: "et",
  FJD: "fj", GMD: "gm", GNF: "gn", GTQ: "gt", HNL: "hn",
  HTG: "ht", JMD: "jm", KGS: "kg", KMF: "km", LSL: "ls",
  LYD: "ly", MGA: "mg", MOP: "mo", MUR: "mu", MWK: "mw",
  MZN: "mz", NAD: "na", NPR: "np", PAB: "pa", PGK: "pg",
  PYG: "py", RWF: "rw", SCR: "sc", SLL: "sl", SOS: "so",
  SRD: "sr", SZL: "sz", TJS: "tj", TMT: "tm", TOP: "to",
  TTD: "tt", UGX: "ug", UYU: "uy", VES: "ve", VUV: "vu",
  WST: "ws", XAF: "cm", XCD: "ag", XOF: "sn", XPF: "pf",
  YER: "ye", ZMW: "zm", ZWL: "zw",
};

// Stock to country mapping
const STOCK_COUNTRIES = {
  AAPL: "us", MSFT: "us", GOOGL: "us", GOOG: "us", AMZN: "us",
  META: "us", FB: "us", NVDA: "us", NFLX: "us", AMD: "us",
  INTC: "us", CRM: "us", ADBE: "us", ORCL: "us", IBM: "us",
  CSCO: "us", QCOM: "us", TXN: "us", AVGO: "us", MU: "us",
  AMAT: "us", LRCX: "us", KLAC: "us", ADI: "us", MCHP: "us",
  NXPI: "us", MRVL: "us", ON: "us", TSLA: "us", PYPL: "us",
  SQ: "us", BLOCK: "us", TWTR: "us", SNAP: "us", UBER: "us",
  LYFT: "us", ZM: "us", PLTR: "us", COIN: "us", RBLX: "us",
  DKNG: "us", PTON: "us", ABNB: "us", CRWD: "us", SNOW: "us",
  DASH: "us", DOCU: "us", OKTA: "us", ZS: "us", NET: "us",
  MDB: "us", DDOG: "us", HUBS: "us", TEAM: "us", WDAY: "us",
  ADSK: "us", ANSS: "us", CDNS: "us", SNPS: "us", FTNT: "us",
  PANW: "us", NOW: "us", VEEV: "us", JPM: "us", BAC: "us",
  WFC: "us", C: "us", GS: "us", MS: "us", V: "us",
  MA: "us", AXP: "us", DFS: "us", COF: "us", USB: "us",
  PNC: "us", TFC: "us", BK: "us", STT: "us", SCHW: "us",
  BLK: "us", WMT: "us", TGT: "us", COST: "us", HD: "us",
  LOW: "us", MCD: "us", SBUX: "us", NKE: "us", DIS: "us",
  KO: "us", PEP: "us", PG: "us", JNJ: "us", MRK: "us",
  PFE: "us", LLY: "us", ABBV: "us", BMY: "us", AMGN: "us",
  GILD: "us", CVS: "us", WBA: "us", UNH: "us", CI: "us",
  HUM: "us", BA: "us", CAT: "us", GE: "us", MMM: "us",
  HON: "us", UPS: "us", FDX: "us", LMT: "us", RTX: "us",
  NOC: "us", GD: "us", DE: "us", EMR: "us", ETN: "us",
  ROK: "us", XOM: "us", CVX: "us", COP: "us", EOG: "us",
  SLB: "us", HAL: "us", KMI: "us", WMB: "us", OKE: "us",
  PSX: "us", T: "us", VZ: "us", TMUS: "us", CMCSA: "us",
  CHTR: "us", F: "us", GM: "us", RIVN: "us", LCID: "us",
  BABA: "cn", BIDU: "cn", JD: "cn", PDD: "cn", NIO: "cn",
  XPEV: "cn", LI: "cn", TME: "cn", NTES: "cn", EDU: "cn",
  FUTU: "cn", BILI: "cn", IQ: "cn", WB: "cn", ZTO: "cn",
  TAL: "cn", YUMC: "cn", TCOM: "cn", VIPS: "cn", DADA: "cn",
  BZUN: "cn", GDS: "cn", KC: "cn", LX: "cn", MOMO: "cn",
  QFIN: "cn", SOHU: "cn", YY: "cn", ZLAB: "cn",
  SAP: "de", VOW3: "de", BMW: "de", MBG: "de", BAYN: "de",
  DTE: "de", ALV: "de", BAS: "de", SIE: "de", IFX: "de",
  DBK: "de", HEN3: "de", RWE: "de", EON: "de", DPW: "de",
  AIR: "fr", BNP: "fr", DG: "fr", KER: "fr", MC: "fr",
  OR: "fr", TTE: "fr", VIE: "fr", NESN: "ch", NOVN: "ch",
  ROG: "ch", UBSG: "ch", ASML: "nl", INGA: "nl", PHIA: "nl",
  UNA: "nl", SAN: "es", BBVA: "es", TEF: "es", ITX: "es",
  IBE: "es", ENI: "it", ENEL: "it", ISP: "it", UCG: "it",
  HSBC: "gb", BP: "gb", GSK: "gb", AZN: "gb", ULVR: "gb",
  RIO: "gb", VOD: "gb", LLOY: "gb", BARC: "gb", BATS: "gb",
  DGE: "gb", PRU: "gb", AV: "gb", STAN: "gb", TSCO: "gb",
  SBRY: "gb", NG: "gb", SSE: "gb", BT: "gb",
  TM: "jp", SONY: "jp", MUFG: "jp", SMFG: "jp", NTT: "jp",
  HMC: "jp", MFG: "jp", CAJ: "jp", SNE: "jp", TAK: "jp",
  RY: "ca", TD: "ca", BNS: "ca", BMO: "ca", CM: "ca",
  ENB: "ca", CNQ: "ca", TRP: "ca", SU: "ca", CP: "ca",
  CNR: "ca", BCE: "ca", MFC: "ca", SLF: "ca",
  BHP: "au", CSL: "au", WBC: "au", NAB: "au", ANZ: "au",
  CBA: "au", MQG: "au", WES: "au", TLS: "au", WOW: "au",
  VALE: "br", PBR: "br", ITUB: "br", BBD: "br", ABEV: "br",
  INFY: "in", WIT: "in", HDB: "in", IBN: "in", TTM: "in",
  TATAMOTORS: "in", TATAMTRDVR: "in", TCS: "in", RELIANCE: "in",
  HINDUNILVR: "in", ADANIENT: "in", ICICIBANK: "in", SBIN: "in",
  BHARTIARTL: "in", MARUTI: "in", HDFCBANK: "in", HDFC: "in",
  KOTAKBANK: "in", AXISBANK: "in", LT: "in", ITC: "in",
  WIPRO: "in", HCLTECH: "in", TECHM: "in", SUNPHARMA: "in",
  DRREDDY: "in", CIPLA: "in", BAJFINANCE: "in", BAJAJFINSV: "in",
  ASIANPAINT: "in", TITAN: "in", ULTRACEMCO: "in", JSWSTEEL: "in",
  TATASTEEL: "in", HINDALCO: "in", VEDL: "in", ONGC: "in",
  NTPC: "in", POWERGRID: "in", IOC: "in", BPCL: "in",
  GAIL: "in", COALINDIA: "in", HEROMOTOCO: "in", EICHERMOT: "in",
  MANDM: "in", GRASIM: "in", SHREECEM: "in", DIVISLAB: "in",
  PIDILITIND: "in", BRITANNIA: "in", NESTLEIND: "in", DABUR: "in",
  GODREJCP: "in", MARICO: "in", UPL: "in",
  ERIC: "se", SPOT: "se", NOK: "fi", AMS: "at", TSM: "tw",
  STM: "fr", SHOP: "ca",
};

// Crypto icons mapping
const CRYPTO_ICONS = {
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.svg",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.svg",
  BNB: "https://cryptologos.cc/logos/bnb-bnb-logo.svg",
  XRP: "https://cryptologos.cc/logos/xrp-xrp-logo.svg",
  ADA: "https://cryptologos.cc/logos/cardano-ada-logo.svg",
  SOL: "https://cryptologos.cc/logos/solana-sol-logo.svg",
  DOGE: "https://cryptologos.cc/logos/dogecoin-doge-logo.svg",
  DOT: "https://cryptologos.cc/logos/polkadot-new-dot-logo.svg",
  LTC: "https://cryptologos.cc/logos/litecoin-ltc-logo.svg",
  LINK: "https://cryptologos.cc/logos/chainlink-link-logo.svg",
  MATIC: "https://cryptologos.cc/logos/polygon-matic-logo.svg",
  AVAX: "https://cryptologos.cc/logos/avalanche-avax-logo.svg",
  UNI: "https://cryptologos.cc/logos/uniswap-uni-logo.svg",
  ATOM: "https://cryptologos.cc/logos/cosmos-atom-logo.svg",
  ETC: "https://cryptologos.cc/logos/ethereum-classic-etc-logo.svg",
  FIL: "https://cryptologos.cc/logos/filecoin-fil-logo.svg",
  APT: "https://cryptologos.cc/logos/aptos-apt-logo.svg",
  ARB: "https://cryptologos.cc/logos/arbitrum-arb-logo.svg",
  OP: "https://cryptologos.cc/logos/optimism-op-logo.svg",
  NEAR: "https://cryptologos.cc/logos/near-protocol-near-logo.svg",
  ALGO: "https://cryptologos.cc/logos/algorand-algo-logo.svg",
  VET: "https://cryptologos.cc/logos/vechain-vet-logo.svg",
  ICP: "https://cryptologos.cc/logos/internet-computer-icp-logo.svg",
  SAND: "https://cryptologos.cc/logos/the-sandbox-sand-logo.svg",
  MANA: "https://cryptologos.cc/logos/decentraland-mana-logo.svg",
  AXS: "https://cryptologos.cc/logos/axie-infinity-axs-logo.svg",
  GALA: "https://cryptologos.cc/logos/gala-gala-logo.svg",
  ENS: "https://cryptologos.cc/logos/ethereum-name-service-ens-logo.svg",
  CRV: "https://cryptologos.cc/logos/curve-dao-token-crv-logo.svg",
  AAVE: "https://cryptologos.cc/logos/aave-aave-logo.svg",
  COMP: "https://cryptologos.cc/logos/compound-comp-logo.svg",
  MKR: "https://cryptologos.cc/logos/maker-mkr-logo.svg",
  SNX: "https://cryptologos.cc/logos/synthetix-snx-logo.svg",
  YFI: "https://cryptologos.cc/logos/yearn-finance-yfi-logo.svg",
  SUSHI: "https://cryptologos.cc/logos/sushiswap-sushi-logo.svg",
  ONE: "https://cryptologos.cc/logos/harmony-one-logo.svg",
  CELO: "https://cryptologos.cc/logos/celo-celo-logo.svg",
  ZEC: "https://cryptologos.cc/logos/zcash-zec-logo.svg",
  XMR: "https://cryptologos.cc/logos/monero-xmr-logo.svg",
  DASH: "https://cryptologos.cc/logos/dash-dash-logo.svg",
  EOS: "https://cryptologos.cc/logos/eos-eos-logo.svg",
  TRX: "https://cryptologos.cc/logos/tron-trx-logo.svg",
  XLM: "https://cryptologos.cc/logos/stellar-xlm-logo.svg",
  HBAR: "https://cryptologos.cc/logos/hedera-hbar-logo.svg",
  QNT: "https://cryptologos.cc/logos/quant-qnt-logo.svg",
  GRT: "https://cryptologos.cc/logos/the-graph-grt-logo.svg",
  CHZ: "https://cryptologos.cc/logos/chiliz-chz-logo.svg",
  ENJ: "https://cryptologos.cc/logos/enjin-coin-enj-logo.svg",
  BAT: "https://cryptologos.cc/logos/basic-attention-token-bat-logo.svg",
  ZRX: "https://cryptologos.cc/logos/0x-zrx-logo.svg",
  KNC: "https://cryptologos.cc/logos/kyber-network-knc-logo.svg",
  REN: "https://cryptologos.cc/logos/ren-ren-logo.svg",
  STORJ: "https://cryptologos.cc/logos/storj-storj-logo.svg",
  ANKR: "https://cryptologos.cc/logos/ankr-ankr-logo.svg",
  CTSI: "https://cryptologos.cc/logos/cartesi-ctsi-logo.svg",
  BAND: "https://cryptologos.cc/logos/band-protocol-band-logo.svg",
  MASK: "https://cryptologos.cc/logos/mask-network-mask-logo.svg",
  INJ: "https://cryptologos.cc/logos/injective-inj-logo.svg",
  KAVA: "https://cryptologos.cc/logos/kava-kava-logo.svg",
  ICX: "https://cryptologos.cc/logos/icon-icx-logo.svg",
  ONT: "https://cryptologos.cc/logos/ontology-ont-logo.svg",
  ZIL: "https://cryptologos.cc/logos/zilliqa-zil-logo.svg",
  THETA: "https://cryptologos.cc/logos/theta-theta-logo.svg",
  HNT: "https://cryptologos.cc/logos/helium-hnt-logo.svg",
  FLOW: "https://cryptologos.cc/logos/flow-flow-logo.svg",
  EGLD: "https://cryptologos.cc/logos/elrond-egld-logo.svg",
  XTZ: "https://cryptologos.cc/logos/tezos-xtz-logo.svg",
  NEO: "https://cryptologos.cc/logos/neo-neo-logo.svg",
  WAVES: "https://cryptologos.cc/logos/waves-waves-logo.svg",
  KSM: "https://cryptologos.cc/logos/kusama-ksm-logo.svg",
  RUNE: "https://cryptologos.cc/logos/thorchain-rune-logo.svg",
  USDT: "https://cryptologos.cc/logos/tether-usdt-logo.svg",
  USDC: "https://cryptologos.cc/logos/usd-coin-usdc-logo.svg",
  BUSD: "https://cryptologos.cc/logos/binance-usd-busd-logo.svg",
  DAI: "https://cryptologos.cc/logos/dai-dai-logo.svg",
};

// Get forex flags
const getForexFlags = (instId) => {
  let cleanId = instId.toUpperCase().replace("USDT", "");
  if (cleanId === "RY") return ["us", "tr"];
  if (cleanId.length === 6) {
    const first = cleanId.substring(0, 3);
    const second = cleanId.substring(3, 6);
    if (CURRENCY_FLAGS[first] && CURRENCY_FLAGS[second]) {
      return [CURRENCY_FLAGS[first], CURRENCY_FLAGS[second]];
    }
  }
  return null;
};

// Get crypto icon
const getCryptoIcon = (instId) => {
  const cleanId = instId.toUpperCase().replace("USDT", "").replace("USD", "");
  return CRYPTO_ICONS[cleanId] || null;
};

// Get stock flag
const getStockFlag = (instId) => {
  const cleanId = instId.toUpperCase().replace("USDT", "").replace("USD", "");
  if (STOCK_COUNTRIES[cleanId]) return STOCK_COUNTRIES[cleanId];
  return "us";
};

// Instrument Icon component
const InstrumentIcon = ({ inst }) => {
  if (inst.cat === "forex") {
    const flags = getForexFlags(inst.id);
    if (!flags) return null;
    return (
      <div style={{ display: "flex", gap: "1px", alignItems: "center", flexShrink: 0 }}>
        <img src={`https://flagcdn.com/w20/${flags[0]}.png`} srcSet={`https://flagcdn.com/w40/${flags[0]}.png 2x`} alt={flags[0]} width={15} height={11} style={{ objectFit: "cover", borderRadius: "1px" }} onError={(e) => { e.target.style.display = "none"; }} />
        <img src={`https://flagcdn.com/w20/${flags[1]}.png`} srcSet={`https://flagcdn.com/w40/${flags[1]}.png 2x`} alt={flags[1]} width={15} height={11} style={{ objectFit: "cover", borderRadius: "1px" }} onError={(e) => { e.target.style.display = "none"; }} />
      </div>
    );
  }
  
  if (inst.cat === "crypto") {
    const iconUrl = getCryptoIcon(inst.id);
    if (!iconUrl) return null;
    return <img src={iconUrl} alt={inst.id} width={15} height={15} style={{ borderRadius: "50%", flexShrink: 0 }} onError={(e) => { e.target.style.display = "none"; }} />;
  }
  
  if (inst.cat === "stock") {
    const countryCode = getStockFlag(inst.id);
    if (!countryCode) return null;
    return <img src={`https://flagcdn.com/w20/${countryCode}.png`} srcSet={`https://flagcdn.com/w40/${countryCode}.png 2x`} alt={countryCode} width={15} height={11} style={{ objectFit: "cover", borderRadius: "1px", flexShrink: 0 }} onError={(e) => { e.target.style.display = "none"; }} />;
  }
  
  return null;
};

const MarketTable = ({ category = 'all', store }) => {
  const dispatch = useDispatch();
  const { marketData, activeInstrument } = useSelector((state) => state.trade);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredInstruments, setFilteredInstruments] = useState(INSTRUMENTS);
  const [priceFlash, setPriceFlash] = useState({});
  const [favorites, setFavorites] = useState(() => {
    try { const saved = localStorage.getItem('marketFavorites'); return saved ? JSON.parse(saved) : []; }
    catch { return []; }
  });
  const prevPricesRef = useRef({});

  useEffect(() => { localStorage.setItem('marketFavorites', JSON.stringify(favorites)); }, [favorites]);

  useEffect(() => {
    const handleSearch = (event) => { if (event.detail) setSearchQuery(event.detail); };
    window.addEventListener('search-symbol', handleSearch);
    return () => window.removeEventListener('search-symbol', handleSearch);
  }, []);

  useEffect(() => {
    let filtered = INSTRUMENTS;
    if (category === 'fav') filtered = INSTRUMENTS.filter((inst) => favorites.includes(inst.id));
    else if (category !== 'all') filtered = INSTRUMENTS.filter((inst) => inst.cat === category);
    if (searchQuery) filtered = filtered.filter((inst) => inst.id.toLowerCase().includes(searchQuery.toLowerCase()) || inst.name.toLowerCase().includes(searchQuery.toLowerCase()));
    setFilteredInstruments(filtered);
  }, [searchQuery, category, favorites]);

  useEffect(() => {
    const flash = {};
    const newPrevPrices = { ...prevPricesRef.current };
    filteredInstruments.forEach((inst) => {
      const currentPrice = marketData[inst.id]?.price;
      const prevPrice = prevPricesRef.current[inst.id];
      if (prevPrice !== undefined && currentPrice !== undefined && prevPrice !== currentPrice) {
        flash[inst.id] = currentPrice > prevPrice ? 'up' : 'dn';
        newPrevPrices[inst.id] = currentPrice;
      } else if (currentPrice !== undefined) newPrevPrices[inst.id] = currentPrice;
    });
    if (Object.keys(flash).length > 0) { setPriceFlash(flash); setTimeout(() => setPriceFlash({}), 650); }
    prevPricesRef.current = newPrevPrices;
  }, [marketData, filteredInstruments]);

  const handleRowClick = (inst) => {
    dispatch(setActiveInstrument(inst));
    window.dispatchEvent(new CustomEvent('instrument-selected', { detail: inst }));
  };

  const toggleFavorite = (e, instrumentId) => {
    e.stopPropagation();
    setFavorites((prev) => prev.includes(instrumentId) ? prev.filter((id) => id !== instrumentId) : [...prev, instrumentId]);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="p-2 border-b flex items-center gap-2 flex-shrink-0" style={{ background: 'var(--color-bgSurface)', borderColor: 'var(--color-borderMedium)' }}>
        <i className="fas fa-search text-xs" style={{ color: 'var(--color-textDim)' }} />
        <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search symbol or name..."
          className="flex-1 bg-transparent border-none text-xs outline-none" style={{ color: 'var(--color-text)' }} />
        {searchQuery && <button onClick={() => setSearchQuery('')} className="transition-colors" style={{ color: 'var(--color-textDim)' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--color-text)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--color-textDim)'}><i className="fas fa-times" /></button>}
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredInstruments.map((inst) => {
          const data = marketData[inst.id];
          const price = data?.price || 0;
          const pct = data?.changePct || 0;
          const isActive = activeInstrument?.id === inst.id;
          const hasFlash = priceFlash[inst.id];
          const favorite = favorites.includes(inst.id);

          return (
            <div key={inst.id} onClick={() => handleRowClick(inst)}
              className="grid grid-cols-[1fr_70px] px-3 py-2 cursor-pointer transition-all duration-150 relative border-l-[3px]"
              style={{ 
                background: isActive ? 'var(--color-orangeBg)' : 'transparent',
                borderBottom: '1px solid var(--color-borderLight)',
                borderLeftColor: isActive ? 'var(--color-orange)' : 'transparent'
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--color-bgHover)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}>
              <div className="flex items-center gap-1.5 relative z-10">
                <button onClick={(e) => toggleFavorite(e, inst.id)} className="text-[10px] transition-colors" style={{ color: favorite ? '#f59e0b' : 'var(--color-textDim)' }}>
                  <i className="fas fa-star" />
                </button>
                <InstrumentIcon inst={inst} />
                <span className="text-xs font-syne font-extrabold" style={{ color: 'var(--color-text)' }}>{inst.id}</span>
              </div>
              <div className="text-right relative z-10">
                <div className={`font-mono text-[11px] font-extrabold ${hasFlash === 'up' ? 'tick-up' : hasFlash === 'dn' ? 'tick-dn' : ''}`} style={{ color: 'var(--color-text)' }}>
                  {fmtP(price, inst.cat)}
                </div>
                <div className="text-[10px] font-mono font-bold" style={{ color: pct > 0 ? 'var(--color-green)' : pct < 0 ? 'var(--color-red)' : 'var(--color-textSecondary)' }}>
                  {pct > 0 ? '+' : ''}{pct.toFixed(2)}%
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredInstruments.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <i className="fas fa-search text-2xl mb-3 opacity-30" style={{ color: 'var(--color-textDim)' }} />
          <p className="font-mono text-xs" style={{ color: 'var(--color-textDim)' }}>{category === 'fav' ? 'No favorite instruments yet' : 'No instruments found'}</p>
        </div>
      )}

      <style>{`
        .tick-up { animation: flashUp 0.65s ease-out forwards; display: inline-block; }
        .tick-dn { animation: flashDn 0.65s ease-out forwards; display: inline-block; }
        @keyframes flashUp { 0% { background: rgba(16,185,129,0.28); color: #34d399; transform: scale(1.05); } 100% { background: transparent; color: inherit; transform: scale(1); } }
        @keyframes flashDn { 0% { background: rgba(239,68,68,0.28); color: #f87171; transform: scale(0.95); } 100% { background: transparent; color: inherit; transform: scale(1); } }
      `}</style>
    </div>
  );
};

export default MarketTable;